import { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent, Ref, useState } from 'react'
import Popover from '@mui/material/Popover'
import TextField from '@mui/material/TextField'
import { useTheme } from '@mui/material/styles'
import { useQuantityEntry } from '../useQuantityEntry'
import QuantityKeypad from './QuantityKeypad'

export type QuantityFieldProps = {
	/** The product being counted, for the field's accessible name. */
	label: string,
	/** Null is blank: nothing entered yet. */
	value: number | null,
	/** Called with the clamped next value, or null when the field is cleared. */
	onChange: ( next: number | null ) => void,
	/** Enter in the field, or Done on the pad — the caller moves on to the next field. */
	onEnter?: () => void,
	inputRef?: Ref<HTMLInputElement>,
	min?: number,
	max?: number,
	disabled?: boolean,
}

/** How wide the field sits. Room for four digits without the label truncating. */
const FIELD_WIDTH = 148

function clamp( value: number, min: number, max: number ): number {
	return Math.min( Math.max( value, min ), max )
}

/**
 * The quantity a picker found, as a field they type into.
 *
 * One control, not a stepper. It starts blank, so every count is one the
 * picker actually entered rather than a default they walked past.
 *
 * Behind the field, whichever way of entering a number suits the person's
 * pointer — see `useQuantityEntry`. At a desk they type into it. On a tablet the
 * field is read-only and opens the on-screen pad instead, which is what keeps
 * the operating system's own numeric keyboard from covering half the screen the
 * moment a gloved thumb lands on it. Both look the same; only what happens on a
 * tap differs.
 */
export default function QuantityField( {
	label,
	value,
	onChange,
	min = 0,
	max = Number.MAX_SAFE_INTEGER,
	disabled = false,
	onEnter,
	inputRef,
}: QuantityFieldProps ) {
	const theme = useTheme()
	const entry = useQuantityEntry()
	const [ keypadAnchor, setKeypadAnchor ] = useState<HTMLElement | null>( null )
	// Digits typed since the keypad opened. Empty means "nothing typed yet", which
	// is what makes the first digit replace the current value instead of appending.
	const [ typed, setTyped ] = useState( '' )
	// What the field shows while it has focus. Null means "show the value" — the
	// field only holds its own text mid-edit, so clearing it to type a fresh
	// number does not immediately snap back to the clamped value.
	const [ draft, setDraft ] = useState<string | null>( null )

	const openKeypad = ( event: MouseEvent<HTMLElement> ) => {
		if ( !disabled ) {
			setKeypadAnchor( event.currentTarget )
			setTyped( '' )
		}
	}

	const closeKeypad = () => {
		setKeypadAnchor( null )
		setTyped( '' )
	}

	const finishKeypad = () => {
		closeKeypad()
		onEnter?.()
	}

	const commitTyped = ( next: string ) => {
		setTyped( next )
		onChange( next === '' ? null : clamp( Number( next ), min, max ) )
	}

	const handleChange = ( event: ChangeEvent<HTMLInputElement> ) => {
		const digits = event.target.value.replace( /[^0-9]/g, '' )
		// The draft is clamped as well as the value, so a picker who types 99 against
		// a request for 2 sees 2 rather than a number the field is quietly ignoring.
		// Empty is left empty: blank, not a count of zero.
		setDraft( digits === '' ? '' : String( clamp( Number( digits ), min, max ) ) )
		onChange( digits === '' ? null : clamp( Number( digits ), min, max ) )
	}

	const handleKeyDown = ( event: KeyboardEvent<HTMLInputElement> ) => {
		if ( event.key === 'Enter' && onEnter ) {
			event.preventDefault()
			onEnter()
		}
	}

	// Whatever they left behind, the field goes back to showing the real value —
	// which is the clamped one, so typing 99 against a target of 4 settles to 4.
	const handleBlur = () => setDraft( null )

	// Focusing selects, so the first keystroke replaces the count rather than
	// appending to it — the same thing the pad's first digit does.
	const selectAll = ( event: FocusEvent<HTMLInputElement> ) => event.target.select()

	const onKeypad = entry === 'keypad'

	return (
		<>
			<TextField
				// No floating label: the list names the column (a header on a wide
				// screen, a label over the field when rows stack), and the input's
				// accessible name says which product it is for.
				value={draft ?? ( value === null ? '' : String( value ) )}
				inputRef={inputRef}
				onKeyDown={onKeypad ? undefined : handleKeyDown}
				disabled={disabled}
				onChange={onKeypad ? undefined : handleChange}
				onBlur={onKeypad ? undefined : handleBlur}
				onFocus={onKeypad ? undefined : selectAll}
				onClick={onKeypad ? openKeypad : undefined}
				slotProps={{
					htmlInput: {
						// Names the product as well as the field: the pad that opens from
						// here is named the same way, and a screen reader moving between
						// them should not have to remember which product it was on.
						'aria-label': `Quantity picked for ${label}`,
						'inputMode': 'numeric',
						// Read-only, not disabled: it still reads out, still takes focus and
						// still looks like a field the picker can act on — it just hands the
						// tap to the pad instead of to the operating system's keyboard.
						'readOnly': onKeypad,
						'sx': { typography: 'h5', textAlign: 'center' },
					},
					input: {
						sx: { bgcolor: 'pickFlow.surface', minHeight: theme.touch.control },
					},
				}}
				sx={{ width: FIELD_WIDTH, flexShrink: 0 }}
			/>

			<Popover
				open={onKeypad && !!keypadAnchor}
				anchorEl={keypadAnchor}
				onClose={closeKeypad}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<QuantityKeypad
					label={label}
					typed={typed}
					onTypedChange={commitTyped}
					onDone={finishKeypad}
				/>
			</Popover>
		</>
	)
}
